import { useState } from "react";
import { useMedia, useWindowSize } from "react-use";
import { useRouter } from "next/router";
import classNames from "classnames";
import { mediaQuery } from "../../utils";
import { Close, Info, Menu, OpenTab, SensibleMark } from "../ui/icons";
import Sidebar from "../ui/Sidebar";
import { AlertBanner } from "../ui/AlertBanner";
import Button from "../ui/design-system/Button";
import { Config } from "../../utils/Config";
import AccountDropdown from "../ui/AccountDropdown";
import Tooltip from "../ui/design-system/Tooltip";
import { server } from "../../state";
import { Subscription } from "../../state/server";

type SidebarProps = {
  breadcrumbs?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
};

export const SidebarWrapper: React.FC<SidebarProps> = (props) => {
  const router = useRouter();
  const { width } = useWindowSize();
  const isMobile = width < 768;
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const isLargerThanPhone = useMedia(mediaQuery.md);

  //QuickStart footer
  const account = server.usePolledAccount();

  const trial = account?.trial;
  const isOnSubscriptionPage = router.asPath === "/account/?t=subscription";
  const trialIsActive = (trial?.days_remaining ?? 0) > 0;
  const trialExpired = account?.plan === Subscription.NoUse;
  const showTrialBanner =
    !isOnSubscriptionPage && (trialIsActive || trialExpired);
  const trialExpiryDays =
    trial &&
    `${trial.days_remaining} day${trial.days_remaining > 1 ? "s" : ""}`;

  const showSidebar = isSidebarOpen && !isLargerThanPhone;
  return (
    <div className="h-full flex flex-col md:flex-row">
      <div className="md:hidden h-16 flex justify-between items-center bg-brand-700 px-2 w-full z-40">
        <button
          title="Toggle sidebar"
          onClick={() => setIsSidebarOpen((isOpen) => !isOpen)}
          className="p-1"
        >
          <span className="text-gray-100 hover:text-gray-200">
            {showSidebar ? <Close /> : <Menu />}
          </span>
        </button>
        {showTrialBanner && isMobile && (
          <div className="text-white text-sm flex flex-row gap-2 items-center">
            <p>
              {trialIsActive
                ? `Your trial expires in ${trialExpiryDays}`
                : "Your trial has expired"}
            </p>
            <a className="text-white" href="/account/?t=subscription">
              <b>
                <u>Upgrade now</u>
              </b>
            </a>
          </div>
        )}
        <span className="text-gray-100 hover:text-gray-200 w-12 h-12 -ml-2">
          <SensibleMark />
        </span>
      </div>
      <div
        className={showSidebar ? "fixed bg-gray-700/30 w-full h-full z-20" : ""}
        onClick={() => setIsSidebarOpen(false)}
      />
      <Sidebar isSidebarOpen={showSidebar} />
      <main
        className={`flex flex-col h-full w-full overflow-auto bg-background-main`}
      >
        {showTrialBanner && width > 768 && (
          <AlertBanner className="text-sm">
            <Info className="flex-shrink-0 w-5 h-5" />
            {trialIsActive ? (
              <p>
                Your trial expires in <b>{trialExpiryDays}</b>.{" "}
                <a
                  href="/account/?t=subscription"
                  className="underline font-semibold hover:text-brand-700"
                >
                  Upgrade now
                </a>{" "}
                or contact{" "}
                <a
                  href="mailto:support@sensible.so?subject=Help%20with%20trial%20testing"
                  className="underline font-semibold hover:text-brand-700"
                >
                  support@sensible.so
                </a>{" "}
                if we can help with your testing.
              </p>
            ) : (
              <p>
                <b>Your trial has expired.</b>{" "}
                <a
                  href="/account/?t=subscription"
                  className="underline font-semibold hover:text-brand-700"
                >
                  Upgrade now
                </a>{" "}
                or contact{" "}
                <a
                  href="mailto:support@sensible.so?subject=Trial%20extension%20request"
                  className="underline font-semibold hover:text-brand-700"
                >
                  support@sensible.so
                </a>{" "}
                to extend your trial.
              </p>
            )}
          </AlertBanner>
        )}
        <div className="flex flex-col justify-center relative md:px-ds-14 md:pt-ds-3 md:pb-ds-1 pt-ds-4 px-ds-4">
          <div className="flex justify-between items-center pb-2 md:pb-0">
            {props.breadcrumbs && (
              <ul className={classNames("list-none my-2 sm:my-0 sm:min-h-8 ")}>
                {props.breadcrumbs}
              </ul>
            )}
            <div className={classNames("flex justify-end items-center gap-4")}>
              {!isMobile && (
                <Button
                  iconPosition={"right"}
                  onClick={() => {
                    const { docs_url } = Config;
                    window.open(docs_url, "_blank");
                  }}
                  icon={<OpenTab />}
                  variant={"link_dark"}
                >
                  Documentation
                </Button>
              )}
              <Tooltip
                delay={1_500}
                placement={"bottom"}
                trigger={<AccountDropdown />}
              >
                <div className={"flex flex-col"}>
                  <span className={"font-semibold"}>Account</span>
                  <span className={"font-normal"}>{account?.name}</span>
                </div>
              </Tooltip>
            </div>
          </div>
        </div>
        <div
          className={`${props.className} md:px-ds-14 md:pb-ds-14 md:pt-ds-4 px-ds-4`}
        >
          {props.children}
        </div>
      </main>
    </div>
  );
};
